// hooks/useTabContext.js
import { useEffect, useContext, useRef } from 'react';
import { FilterContext } from 'components/FilterBar/FilterProvider';

/**
 * Custom hook to manage the current tab and sub-tab context
 * @param {string} tabName - The name of the current tab
 * @param {string|null} subTabName - The name of the current sub-tab (optional)
 *
 * Smart behavior:
 * - Child components with specific subTabName use useLayoutEffect (runs first)
 * - Parent components with null subTabName use useEffect (runs after)
 * - This ensures child values always take precedence regardless of mount order
 */

export const useTabContext = (tabName, subTabName = null) => {
  const { setCurrentTabName, setCurrentSubTabName } = useContext(FilterContext);
  const hasSpecificSubTab = subTabName !== null;
  const mountedWithSpecificSubTabRef = useRef(hasSpecificSubTab);

  // Use useLayoutEffect for child components (specific subTab)
  // This runs synchronously before useEffect, ensuring child sets value first
  useEffect(() => {
    if (hasSpecificSubTab) {
      setCurrentTabName(tabName);
      setCurrentSubTabName(subTabName);
    }
  }, [
    hasSpecificSubTab,
    tabName,
    subTabName,
    setCurrentTabName,
    setCurrentSubTabName,
  ]);

  // Use useEffect for parent components (null subTab)
  // This runs after useLayoutEffect, so child values are already set
  useEffect(() => {
    if (!hasSpecificSubTab) {
      setCurrentTabName(tabName);
      // Parent doesn't set subTabName - let child values persist
    }
  }, [hasSpecificSubTab, tabName, setCurrentTabName]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Only cleanup subTabName if this component was mounted with a specific subTab
      // Reset subTabName to null so parent tab's context (without subTab) remains active
      // Don't cleanup if we're a parent (null) - child values should persist
      if (mountedWithSpecificSubTabRef.current) {
        setCurrentSubTabName(null);
        // Don't reset tabName - parent tab component still needs it
      }
    };
  }, [setCurrentSubTabName]);
};
